import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ── Send campaign ──────────────────────────────────────────────────────────────

export interface SendResult {
  targeted:          number;
  eligible:          number;
  sent:              number;
  failed:            number;
  skipped_optout:    number;
  skipped_cooldown:  number;
  mode?:             'test';
}

interface SendCampaignVars {
  campaignId:   string;
  testMode?:    boolean;
  retryFailed?: boolean;  // reenvio de mensagens com status 'failed'
}

export function useSendCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ campaignId, testMode = false, retryFailed = false }: SendCampaignVars): Promise<SendResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão expirada. Faça login novamente.');

      const res = await supabase.functions.invoke('send-campaign', {
        body: {
          campaign_id: campaignId,
          ...(testMode    ? { test_mode:    true } : {}),
          ...(retryFailed ? { retry_failed: true } : {}),
        },
      });

      if (res.error) throw new Error(res.error.message);

      const body = res.data as { ok: boolean; data?: SendResult; error?: string };
      if (!body.ok) throw new Error(body.error ?? 'Falha no disparo');

      return body.data!;
    },
    onSuccess: (_data, vars) => {
      // Invalida queries para forçar refetch imediato
      qc.invalidateQueries({ queryKey: ['campaign', vars.campaignId] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign-messages', vars.campaignId] });
    },
  });
}

// ── Preflight check ──────────────────────────────────────────────────────────
// Consulta WhatsApp status + quotas do plano sem precisar resolver a audiência.
// Usado pelo CampaignDispatchModal para exibir o estado antes do disparo.

export interface PreflightData {
  waStatus:        'active' | 'pending' | 'suspended' | 'inactive' | null;
  waPhoneNumber:   string | null;
  sentToday:       number;
  sentMonth:       number;
  dailyLimit:      number;
  monthlyLimit:    number;
  maxCampaignSize: number;
}

export function usePreflightCheck(enabled: boolean) {
  return useQuery<PreflightData>({
    queryKey: ['campaign-preflight'],
    queryFn: async (): Promise<PreflightData> => {
      const today      = new Date(); today.setHours(0, 0, 0, 0);
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      const [waRes, todayRes, monthRes, limitsRes] = await Promise.all([
        db.from('tenant_whatsapp')
          .select('status, phone_number_id')
          .maybeSingle(),
        supabase.from('campaigns')
          .select('stats_sent')
          .eq('type', 'whatsapp')
          .in('status', ['running', 'completed'])
          .gte('started_at', today.toISOString()),
        supabase.from('campaigns')
          .select('stats_sent')
          .eq('type', 'whatsapp')
          .in('status', ['running', 'completed'])
          .gte('started_at', monthStart.toISOString()),
        db.from('tenant_plan_limits')
          .select('daily_message_limit, monthly_message_limit, max_campaign_size')
          .maybeSingle(),
      ]);

      const sentToday  = ((todayRes.data ?? []) as { stats_sent: number }[])
        .reduce((acc, r) => acc + (r.stats_sent ?? 0), 0);
      const sentMonth  = ((monthRes.data ?? []) as { stats_sent: number }[])
        .reduce((acc, r) => acc + (r.stats_sent ?? 0), 0);
      const limits     = limitsRes.data as { daily_message_limit: number; monthly_message_limit: number; max_campaign_size: number } | null;

      return {
        waStatus:        waRes.data?.status    ?? null,
        waPhoneNumber:   waRes.data?.phone_number_id ?? null,
        sentToday,
        sentMonth,
        dailyLimit:      limits?.daily_message_limit   ?? 250,
        monthlyLimit:    limits?.monthly_message_limit ?? 5000,
        maxCampaignSize: limits?.max_campaign_size     ?? 250,
      };
    },
    enabled,
    staleTime: 30_000,
  });
}

// ── Message stats (polling quando running) ─────────────────────────────────────

export interface MessageStats {
  total:     number;
  queued:    number;
  sent:      number;
  delivered: number;
  read:      number;
  failed:    number;
}

export function useCampaignMessageStats(campaignId: string | undefined, isRunning: boolean) {
  return useQuery({
    queryKey: ['campaign-messages', campaignId, 'stats'],
    queryFn: async (): Promise<MessageStats> => {
      const { data, error } = await (supabase as any)
        .from('campaign_messages')
        .select('status')
        .eq('campaign_id', campaignId);

      if (error) throw error;

      const rows = (data ?? []) as { status: string }[];
      return {
        total:     rows.length,
        queued:    rows.filter((r) => r.status === 'queued').length,
        sent:      rows.filter((r) => r.status === 'sent').length,
        delivered: rows.filter((r) => r.status === 'delivered').length,
        read:      rows.filter((r) => r.status === 'read').length,
        failed:    rows.filter((r) => r.status === 'failed').length,
      };
    },
    enabled: !!campaignId,
    // Polling a cada 8 s quando campanha está em andamento
    refetchInterval: isRunning ? 8_000 : false,
    staleTime: isRunning ? 0 : 30_000,
  });
}

// ── Test-mode detection ────────────────────────────────────────────────────────
// Retorna true se a campanha foi disparada em modo simulação:
// todas as mensagens enviadas têm wa_message_id NULL (sem chamada real à API).

export function useIsTestMode(campaignId: string | undefined, hasSent: boolean) {
  return useQuery<boolean>({
    queryKey: ['campaign-is-test-mode', campaignId],
    queryFn: async (): Promise<boolean> => {
      // Conta mensagens com wa_message_id preenchido
      const { count } = await (supabase as any)
        .from('campaign_messages')
        .select('wa_message_id', { count: 'exact', head: true })
        .eq('campaign_id', campaignId)
        .not('wa_message_id', 'is', null);

      // Se nenhuma mensagem tem wa_message_id → disparo foi simulado
      return (count ?? 0) === 0;
    },
    enabled: !!campaignId && hasSent,
    staleTime: 60_000,
  });
}

// ── Individual messages list ───────────────────────────────────────────────────

export function useCampaignMessages(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-messages', campaignId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('campaign_messages')
        .select(`
          id, phone, status, error_code, error_title,
          sent_at, delivered_at, read_at, failed_at,
          contact:contacts(id, full_name)
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: true })
        .limit(200);

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!campaignId,
  });
}

